import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import useGetUnloadCategoriesTrend from '@/queries/analytics/useGetUnloadCategoriesTrend';
import { getColor } from '@/utils/colors';

import Container from '../components/Container';
import { TotalsBarChartTooltip } from '../components/CustomTooltip';
import Loader from '../components/Loader';

import styles from './UnloadsCategoriesBarChart.module.scss';

const defaultFrom: string = new Date(new Date().getDate() - 7).toISOString();
const defaultTo: string = new Date().toISOString();

type UnloadsCategoriesBarChartProps = { from?: string; to?: string };

const UnloadsCategoriesBarChart = ({
  from = defaultFrom,
  to = defaultTo,
}: UnloadsCategoriesBarChartProps) => {
  const { t } = useTranslation();
  const { data: result } = useGetUnloadCategoriesTrend(from, to);

  if (!result) {
    return <Loader />;
  }

  const data = result.map((res) => ({
    ...res,
    fill: getColor(res.name || 'Other'),
    label: res.name || t('widgets.totalByCategory'),
  }));

  if (!data.length) {
    return (
      <Container className={styles.emptyState}>
        <span className={styles.message}>{t('commons.emptyResult')}</span>
      </Container>
    );
  }

  return (
    <Container className={styles.container}>
      <h5 className={styles.legend}>{t('widgets.totalByCategory')}</h5>
      <ResponsiveContainer
        width="100%"
        height={145}
        className={styles.responsiveContainer}
      >
        <BarChart data={data} maxBarSize={20}>
          <XAxis dataKey="name" />
          <Tooltip cursor={false} content={<TotalsBarChartTooltip />} />
          <Bar dataKey="total" />
        </BarChart>
      </ResponsiveContainer>
    </Container>
  );
};

export default UnloadsCategoriesBarChart;
